import React from 'react';
import styled from 'styled-components';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

export default function Services({ data }) {
  if (!data) {
    return null;
  }

  const isEmptyObject = (obj) => {
    try {
      for (var key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          return false;
        }
      }
      return true;
    } catch (_err) {
      return true;
    }
  };

  const { 
    primary_intervention_type,
    primary_intervention_subtypes,
    primary_intervention_description,
    intervention_dosage_type,
    intervention_dosage_level,
    intervention_dosage_number,
    taxonomy_subject_level,
    services = [],
    strategies = [],
  } = data;

  const interventionTypeName = typeof primary_intervention_type === 'string'
    ? primary_intervention_type
    : primary_intervention_type?.values?.name;
  const interventionSubtypeName = typeof primary_intervention_subtypes === 'string'
    ? primary_intervention_subtypes
    : primary_intervention_subtypes?.values?.[0]?.description;

  const isEmpty = (
    isEmptyObject(primary_intervention_type) &&
    isEmptyObject(primary_intervention_subtypes) &&
    isEmptyObject(primary_intervention_description) &&
    isEmptyObject(intervention_dosage_type) &&
    isEmptyObject(intervention_dosage_level) &&
    isEmptyObject(intervention_dosage_number) &&
    isEmptyObject(taxonomy_subject_level) &&
    services.length===0 &&
    strategies.length===0);

  if (isEmpty) {
    return (
    <Container>
      <Table>
        <Heading>
          <h2>Core Services</h2>
          <p>No core services loaded yet</p>
        </Heading>
      </Table>
    </Container>
    );
  }

  return (
    <Container>
      <Table>
        <Heading>
          <h2>Core Services</h2>
          <p>{'Below is the grantee\'s description of each of the core services or activities this program/initiative uses to achieve ' +
            'its goal.'}</p>

          {taxonomy_subject_level?.values && (<Title>{'The program\'s primary core service or activity:'}</Title>)}
          <p>
            { taxonomy_subject_level?.values?.[0]?.Subject && (
              <Tooltip title={taxonomy_subject_level?.values?.[0]?.Definition}>
                <StyledChip label={taxonomy_subject_level?.values?.[0]?.Subject}/>
              </Tooltip>
            )}
            { taxonomy_subject_level?.values?.[1]?.Subject && (
            <Tooltip title={taxonomy_subject_level?.values?.[1]?.Definition}>
              <StyledChip label={taxonomy_subject_level?.values?.[1]?.Subject}/>
            </Tooltip>
            )}
          </p>

          { interventionTypeName && (<Title>The primary and secondary intervention types:</Title>)}
          { interventionTypeName && (
            <p>
              <Table style={{minHeight: '0px', 'paddingBottom': '0px'}}>
                <TableHeader>
                  <div><span>Primary</span></div>
                  <div><span>Secondary</span></div>
                </TableHeader>
                <TableRow>
                  <div><span>{interventionTypeName}</span></div>
                  <div><span>{interventionSubtypeName}</span></div>
                </TableRow>
              </Table>
            </p>
          )}

          { intervention_dosage_type && (<Title>{'The program\'s duration and frequency of engagement with beneficiaries:'}</Title>)}
          { intervention_dosage_type && (
            <p>
              <Table style={{minHeight: '0px', 'paddingBottom': '0px'}}>
                <TableHeader>
                  <div><span>Program Duration</span></div>
                  <div><span>Engagement Frequency</span></div>
                  <div><span>Engagement Duration</span></div>
                </TableHeader>
                <TableRow>
                  <div><span>{intervention_dosage_type}</span></div>
                  <div><span>{intervention_dosage_number}</span></div>
                  <div><span>{intervention_dosage_level}</span></div>
                </TableRow>
              </Table>
            </p>
          )}

          { primary_intervention_description && (<Title>{'Additional information about your program\'s core service, activity or ' +
            'strategy:'}</Title>)}
          { primary_intervention_description && (<p>{primary_intervention_description}</p>)}

          { strategies && strategies.length > 0 && (<Title>{'Your program\'s core services or activities (previously entered):'}</Title>)}
          { strategies && strategies.length > 0 && (
            <ul>
              {strategies.map(st => <ListItem key={st}>{st}</ListItem>)}
            </ul>
          )}

        </Heading>
      </Table>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const ListItem = styled.li`
  color: #595959;
  font-size: 14px;
  list-style: square;
  margin-left: 0px; 
`;

const Table = styled.div`
  width: 100%;
  height: 100%;
  min-height: 200px;
  background-color: #FFFFFF;
  border: solid 1px #ccd7e3;
  border-radius: 4px;
  padding-bottom: 20px; 
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
`;

const Heading = styled.div`
  padding: 20px;
  padding-bottom: 0px;
  h2 {
    color: #595959;
    font-size: 24px;
    font-weight: 700;
  }
  p {
    color: #595959;
    font-size: 14px;
    font-weight: 400;
  }
`;

const TableRow = styled.div`
  width: 100%;
  height: auto;
  padding: 20px;
  background-color: #f5f8fa;
  border-top: solid 1px #ccd7e3;
  border-bottom: solid 1px #ccd7e3;
  display: flex;
  flex-direction: row;
  div {
    span {
      text-transform: capitalize;
    }
  }
  div:nth-child(1) {
    flex: 1;
  }
  div:nth-child(2) {
    flex: 1;
  }
`;

const TableHeader = styled.div`
  width: 100%;
  height: auto;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 10px;
  display: flex;
  flex-direction: row;
  div {
    border-left: 1px solid #ccd7e3;
    padding-left: 5px;
    span {
      font-size: 12px;
      color: #46586b;
      font-weight: 700;
    }
  }
  div:nth-child(1) {
    flex: 1;
    border-left: none;
  }
  div:nth-child(2) {
    flex: 1;
  }
  div:nth-child(3) {
    flex: 1;
  }
  div:nth-child(4) {
    flex: 1;
  }
`;

const StyledChip = styled(Chip)`
  margin-left: 10px;
`;